// src/pages/legal/TermsOfService.jsx
import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet";

/**
 * TermsOfService
 * - React-Bootstrap layout with clean spacing
 * - Friendly, plain-English terms typical for a SaaS/content site
 * - Not legal advice (customize as needed; have counsel review)
 */
export default function TermsOfService() {
  return (
    <>
      <Helmet>
        <title>Terms of Service • MyGolfDNA</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Container className="py-4">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <header className="mb-4">
              <h1 className="h2 mb-2">Terms of Service</h1>
              <p className="text-muted mb-0">Last updated: November 5, 2025</p>
            </header>

            <section id="acceptance" className="mb-4">
              <h2 className="h4">1) Acceptance of Terms</h2>
              <p>
                These Terms of Service (“Terms”) govern your access to and use of the MyGolfDNA
                website, content, and services (collectively, the “Services”). By creating an account,
                accessing, or using the Services, you agree to these Terms and our{" "}
                <a href="/policies/privacy">Privacy Policy</a>. If you do not agree, do not use the Services.
              </p>
            </section>

            <section id="eligibility" className="mb-4">
              <h2 className="h4">2) Eligibility & Accounts</h2>
              <ul>
                <li>You must be at least 13 years old to use the Services.</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                <li>You are responsible for all activities that occur under your account.</li>
              </ul>
            </section>

            <section id="subscriptions" className="mb-4">
              <h2 className="h4">3) Subscriptions, Payments & Refunds</h2>
              <ul>
                <li>
                  Some features require a paid subscription. By subscribing, you authorize us and
                  our payment processor to charge your payment method on a recurring basis until
                  you cancel.
                </li>
                <li>
                  Prices, features, and billing cycles may change; we will provide notice of material
                  changes as required by law or these Terms.
                </li>
                <li>
                  Unless stated otherwise, all fees are non‑refundable, except where required by law.
                </li>
              </ul>
            </section>

            <section id="content" className="mb-4">
              <h2 className="h4">4) Content & License</h2>
              <p>
                The Services include content owned by MyGolfDNA and its licensors, including videos,
                text, graphics, trademarks, and logos (“MyGolfDNA Content”). MyGolfDNA grants you a
                limited, non‑exclusive, non‑transferable, revocable license to access and view the
                MyGolfDNA Content solely for your personal, non‑commercial use in connection with
                the Services. No rights are granted except as expressly set out in these Terms.
              </p>
            </section>

            <section id="usercontent" className="mb-4">
              <h2 className="h4">5) User Content</h2>
              <p>
                If you upload or submit content (e.g., comments, swing videos, feedback), you
                represent that you have all necessary rights to do so and you grant MyGolfDNA a
                worldwide, non‑exclusive, royalty‑free license to host, store, display, reproduce,
                modify, and create derivative works for the purpose of operating and improving the
                Services. You remain responsible for your content.
              </p>
            </section>

            <section id="acceptableuse" className="mb-4">
              <h2 className="h4">6) Acceptable Use</h2>
              <ul>
                <li>Do not attempt to copy, redistribute, or make available MyGolfDNA Content outside the Services.</li>
                <li>Do not reverse engineer, decompile, or otherwise attempt to derive source code.</li>
                <li>Do not engage in illegal, infringing, or harmful activity.</li>
                <li>Do not interfere with or disrupt the Services or networks connected to the Services.</li>
              </ul>
            </section>

            <section id="privacy" className="mb-4">
              <h2 className="h4">7) Privacy</h2>
              <p>
                Your use of the Services is also governed by our{" "}
                <a href="/policies/privacy">Privacy Policy</a> which explains how we collect, use, and share
                information.
              </p>
            </section>

            <section id="termination" className="mb-4">
              <h2 className="h4">8) Termination</h2>
              <p>
                We may suspend or terminate your access to the Services at any time for any reason,
                including violation of these Terms. You may cancel your subscription at any time via
                your account settings.
              </p>
            </section>

            <section id="disclaimers" className="mb-4">
              <h2 className="h4">9) Disclaimers</h2>
              <p>
                THE SERVICES AND ALL CONTENT ARE PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT
                WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON‑INFRINGEMENT.
              </p>
              <p>
                Golf instruction and training results vary by individual. Always consult a professional
                regarding any health or physical considerations.
              </p>
            </section>

            <section id="limitation" className="mb-4">
              <h2 className="h4">10) Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, MYGOLFDNA AND ITS AFFILIATES SHALL
                NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
                PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING OUT
                OF OR IN CONNECTION WITH THE SERVICES.
              </p>
            </section>

            <section id="indemnity" className="mb-4">
              <h2 className="h4">11) Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless MyGolfDNA and its affiliates, officers,
                employees, and agents from any claims, liabilities, damages, losses, and expenses,
                including reasonable attorneys’ fees, arising from or related to your use of the
                Services or violation of these Terms.
              </p>
            </section>

            <section id="changes" className="mb-4">
              <h2 className="h4">12) Changes to the Services or Terms</h2>
              <p>
                We may modify the Services and these Terms from time to time. If we make material
                changes, we will provide notice as required. Your continued use after changes
                constitutes acceptance.
              </p>
            </section>

            <section id="law" className="mb-4">
              <h2 className="h4">13) Governing Law</h2>
              <p>
                These Terms are governed by the laws of the State of Florida, without regard to its
                conflict of law principles. Venue for any disputes shall be the state or federal courts
                located in Florida.
              </p>
            </section>

            <section id="contact" className="mb-4">
              <h2 className="h4">14) Contact</h2>
              <address className="mb-0">
                <div className="fw-semibold">MyGolfDNA LLC</div>
                <div>Email: <a href="mailto:support@mygolfdna.com">support@mygolfdna.com</a></div>
              </address>
            </section>

            <footer className="pt-3 mt-4 border-top text-muted">
              <small>&copy; {new Date().getFullYear()} MyGolfDNA LLC. These Terms are provided as general information and are not legal advice.</small>
            </footer>
          </Col>
        </Row>
      </Container>
    </>
  );
}
